import {
  Box,
  Text,
  WizardStep,
  WizardStepRenderParams,
  useOnNext,
} from '@factorialco/deprecated-design-system'
import { DatePickerValue, F0DatePicker } from '@factorialco/f0-react'
import { NumberInput } from '@factorialco/f0-react/dist/experimental'
import moment from 'moment'

import i18n from 'modules/core/lib/i18n'
import { isNumber } from 'modules/core/lib/validateNumberInput'

import { datePickerDateToString, stringDateToDatePicker } from 'modules/finance/lib/budgetDateUtils'
import {
  TrainingsWizardData,
  TrainingsWizardDataField,
  validateMandatoryFields,
} from 'modules/finance/components/TrainingsBudgetWizard/lib/trainingsBudgetWizard'

const AmountsAndDatesStep = ({
  data,
  onDataChanged,
}: WizardStepRenderParams<TrainingsWizardData>) => {
  const handleNext = async () => {
    const mandatoryFields: TrainingsWizardDataField[] = ['effectiveFrom', 'amountCents']

    const { invalidFields, dataWithErrors } = validateMandatoryFields(mandatoryFields, data)

    onDataChanged(dataWithErrors)

    if (invalidFields.length) {
      return Promise.reject()
    }

    // Validate amount
    if (!isNumber(data.amountCents) || Number(data.amountCents) <= 0) {
      onDataChanged({
        ...dataWithErrors,
        errors: {
          ...dataWithErrors.errors,
          amountCents: i18n.t('budgets.forms.errors.invalid_amount'),
        },
      })
      return Promise.reject()
    }

    // Validate dates
    if (data.effectiveUntil && moment(data.effectiveFrom).isAfter(moment(data.effectiveUntil))) {
      onDataChanged({
        ...dataWithErrors,
        errors: {
          ...dataWithErrors.errors,
          effectiveFrom: i18n.t('budgets.forms.errors.start_date'),
        },
      })
      return Promise.reject()
    }
  }

  useOnNext(handleNext)

  const handleStartDateChange = (
    value: DatePickerValue | undefined,
    _stringValue: string | undefined
  ) => {
    onDataChanged({ ...data, effectiveFrom: datePickerDateToString(value) ?? '' })
  }

  const handleEndDateChange = (
    value: DatePickerValue | undefined,
    _stringValue: string | undefined
  ) => {
    onDataChanged({ ...data, effectiveUntil: datePickerDateToString(value) })
  }

  return (
    <Box gap="s40" flexDirection="column">
      <Box gap="s24" width="full">
        <Box gap="s8">
          <Text size="500" weight="semibold">
            {i18n.t('budgets.forms.trainings_budget.amounts_and_dates.title')}
          </Text>
          <Text size="300">
            {i18n.t('budgets.forms.trainings_budget.amounts_and_dates.description')}
          </Text>
        </Box>
      </Box>
      <Box gap="s24" maxWidth="s600" flexDirection="column">
        <NumberInput
          required
          label={i18n.t('budgets.forms.new_budget.budget_amounts.amount.title')}
          locale={i18n.locale}
          units={data.currency}
          onChange={(value) =>
            onDataChanged({ ...data, amountCents: value !== null ? String(value) : '' })
          }
          error={data.errors?.amountCents}
          value={data.amountCents ? Number(data.amountCents) : null}
          maxDecimals={2}
          size="md"
        />

        <Box flexDirection="row" gap="s16">
          <Box style={{ flex: '1' }}>
            <F0DatePicker
              required
              label={i18n.t('budgets.forms.new_budget.budget_limits.start_date')}
              value={stringDateToDatePicker(data.effectiveFrom)}
              onChange={handleStartDateChange}
              placeholder={i18n.t('budgets.forms.new_budget.budget_limits.start_date_placeholder')}
              size="md"
              error={data.errors?.effectiveFrom}
            />
          </Box>
          <Box style={{ flex: '1' }}>
            <F0DatePicker
              label={i18n.t('budgets.forms.new_budget.budget_limits.end_date')}
              value={stringDateToDatePicker(data.effectiveUntil)}
              onChange={handleEndDateChange}
              placeholder={i18n.t('budgets.forms.new_budget.budget_limits.end_date_placeholder')}
              size="md"
              minDate={data.effectiveFrom ? moment(data.effectiveFrom).toDate() : undefined}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export const amountsAndDatesStep = (): WizardStep<TrainingsWizardData> => ({
  title: i18n.t('budgets.forms.trainings_budget.amounts_and_dates.title'),
  nextText: i18n.t('common.next'),
  render: (props) => <AmountsAndDatesStep {...props} />,
  isCompleted: () => true,
})
